import { useState, useEffect } from 'react'
import { authApi, departmentsApi, feedbackApi } from '../api/client'
import { useAuth } from '../hooks/useAuth'
import { HiUserGroup, HiShieldCheck, HiOfficeBuilding, HiPlus, HiTrash, HiPencil, HiUserAdd, HiInformationCircle, HiChevronDown, HiChevronUp, HiCheck, HiX as HiXMark, HiAnnotation, HiLightBulb, HiExclamation, HiChat, HiDotsHorizontal } from 'react-icons/hi'
import UserInviteModal from '../components/admin/UserInviteModal'

function UserRow({ user, onUpdateRole }) {
  const [role, setRole] = useState(user.role)

  const handleRoleChange = async (newRole) => {
    setRole(newRole)
    await onUpdateRole(user.id, newRole)
  }

  return (
    <tr className="border-b border-surface-200 dark:border-surface-700">
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 rounded-full flex items-center justify-center font-medium text-sm">
            {user.display_name?.charAt(0) || 'U'}
          </div>
          <div>
            <p className="font-medium text-surface-800 dark:text-surface-200">{user.display_name}</p>
            <p className="text-sm text-surface-500 dark:text-surface-400">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="py-3 px-4">
        <select
          value={role}
          onChange={(e) => handleRoleChange(e.target.value)}
          className="input py-1 text-sm"
        >
          <option value="member">Member</option>
          <option value="manager">Manager</option>
          <option value="admin">Admin</option>
        </select>
      </td>
      <td className="py-3 px-4 text-sm text-surface-500 dark:text-surface-400">
        {user.departments?.map(d => d.name).join(', ') || 'None'}
      </td>
    </tr>
  )
}

function DepartmentSection({ departments, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false)
  const [editingDept, setEditingDept] = useState(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)

  const handleCreate = async (e) => {
    e.preventDefault()
    if (!name.trim()) return

    setLoading(true)
    try {
      await departmentsApi.create({ name: name.trim(), description: description.trim() })
      setName('')
      setDescription('')
      setShowCreate(false)
      onRefresh()
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (deptId) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return
    try {
      await departmentsApi.delete(deptId)
      onRefresh()
    } catch (err) {
      console.error('Failed to delete department:', err)
    }
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <HiOfficeBuilding className="w-5 h-5 text-brand-500" />
          <h2 className="font-semibold text-surface-800 dark:text-surface-200">Departments</h2>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn btn-primary btn-sm"
        >
          <HiPlus className="w-4 h-4 mr-1" />
          Add
        </button>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="mb-4 p-4 bg-surface-50 dark:bg-surface-800 rounded-lg">
          <div className="space-y-3">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Department name"
              className="input"
              autoFocus
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description (optional)"
              rows={2}
              className="input resize-none"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreate(false)
                  setName('')
                  setDescription('')
                }}
                className="btn btn-secondary btn-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!name.trim() || loading}
                className="btn btn-primary btn-sm"
              >
                Create
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {departments.map(dept => (
          <div
            key={dept.id}
            className="flex items-center justify-between p-3 bg-surface-50 dark:bg-surface-800 rounded-lg"
          >
            <div>
              <p className="font-medium text-surface-800 dark:text-surface-200">{dept.name}</p>
              {dept.description && (
                <p className="text-sm text-surface-500 dark:text-surface-400">{dept.description}</p>
              )}
              <p className="text-xs text-surface-400 dark:text-surface-500 mt-1">
                {dept.member_count || 0} members
              </p>
            </div>
            <button
              onClick={() => handleDelete(dept.id)}
              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
            >
              <HiTrash className="w-4 h-4" />
            </button>
          </div>
        ))}
        {departments.length === 0 && (
          <p className="text-center text-surface-500 dark:text-surface-400 py-4">No departments yet</p>
        )}
      </div>
    </div>
  )
}

const ROLE_PERMISSIONS = [
  { action: 'View projects & tasks', admin: true, manager: true, member: true, viewer: true },
  { action: 'Create & edit own tasks', admin: true, manager: true, member: true, viewer: false },
  { action: 'Comment on tasks', admin: true, manager: true, member: true, viewer: false },
  { action: 'Create projects', admin: true, manager: true, member: false, viewer: false },
  { action: 'Manage all tasks', admin: true, manager: true, member: false, viewer: false },
  { action: 'Invite users', admin: true, manager: true, member: false, viewer: false },
  { action: 'Manage departments', admin: true, manager: false, member: false, viewer: false },
  { action: 'Assign roles', admin: true, manager: false, member: false, viewer: false },
  { action: 'Organization settings', admin: true, manager: false, member: false, viewer: false },
]

function RolePermissionsCard() {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <HiInformationCircle className="w-5 h-5 text-brand-500" />
          <h2 className="font-semibold text-surface-800 dark:text-surface-200">Role Permissions</h2>
        </div>
        {expanded ? (
          <HiChevronUp className="w-5 h-5 text-surface-400" />
        ) : (
          <HiChevronDown className="w-5 h-5 text-surface-400" />
        )}
      </button>

      {expanded && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200 dark:border-surface-700">
                <th className="text-left py-2 px-3 font-medium text-surface-600 dark:text-surface-400">Permission</th>
                <th className="text-center py-2 px-3 font-medium text-surface-600 dark:text-surface-400">Admin</th>
                <th className="text-center py-2 px-3 font-medium text-surface-600 dark:text-surface-400">Manager</th>
                <th className="text-center py-2 px-3 font-medium text-surface-600 dark:text-surface-400">Member</th>
                <th className="text-center py-2 px-3 font-medium text-surface-600 dark:text-surface-400">Viewer</th>
              </tr>
            </thead>
            <tbody>
              {ROLE_PERMISSIONS.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-surface-50 dark:bg-surface-900/50' : ''}>
                  <td className="py-2 px-3 text-surface-700 dark:text-surface-300">{row.action}</td>
                  {['admin', 'manager', 'member', 'viewer'].map((role) => (
                    <td key={role} className="py-2 px-3 text-center">
                      {row[role] ? (
                        <HiCheck className="w-4 h-4 text-green-500 mx-auto" />
                      ) : (
                        <HiXMark className="w-4 h-4 text-red-400 mx-auto" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const CATEGORY_META = {
  bug: { icon: HiExclamation, color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-900/20', label: 'Bug' },
  feature: { icon: HiLightBulb, color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-900/20', label: 'Feature' },
  general: { icon: HiChat, color: 'text-blue-500', bg: 'bg-blue-50 dark:bg-blue-900/20', label: 'General' },
  other: { icon: HiDotsHorizontal, color: 'text-surface-500', bg: 'bg-surface-100 dark:bg-surface-700', label: 'Other' },
}

function FeedbackSection() {
  const [feedbackItems, setFeedbackItems] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchFeedback()
  }, [])

  async function fetchFeedback() {
    try {
      setLoading(true)
      const res = await feedbackApi.list()
      setFeedbackItems(res.data)
    } catch (err) {
      console.error('Failed to fetch feedback:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this feedback?')) return
    try {
      await feedbackApi.delete(id)
      setFeedbackItems((prev) => prev.filter((f) => f.id !== id))
    } catch (err) {
      console.error('Failed to delete feedback:', err)
    }
  }

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        <HiAnnotation className="w-5 h-5 text-brand-500" />
        <h2 className="font-semibold text-surface-800 dark:text-surface-200">
          User Feedback ({feedbackItems.length})
        </h2>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-6 h-6 border-3 border-brand-500 border-t-transparent rounded-full"></div>
        </div>
      ) : feedbackItems.length === 0 ? (
        <p className="text-surface-400 dark:text-surface-500 text-sm py-6 text-center">
          No feedback submitted yet.
        </p>
      ) : (
        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {feedbackItems.map((fb) => {
            const meta = CATEGORY_META[fb.category] || CATEGORY_META.other
            const Icon = meta.icon
            return (
              <div
                key={fb.id}
                className="border border-surface-200 dark:border-surface-700 rounded-lg p-4 hover:bg-surface-50 dark:hover:bg-surface-800/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${meta.bg} ${meta.color} shrink-0 mt-0.5`}>
                      <Icon className="w-3 h-3" />
                      {meta.label}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-surface-800 dark:text-surface-200 whitespace-pre-wrap break-words">
                        {fb.message}
                      </p>
                      <p className="text-xs text-surface-400 dark:text-surface-500 mt-2">
                        {fb.user?.display_name || 'Unknown'} &middot; {new Date(fb.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(fb.id)}
                    className="p-1 text-surface-400 hover:text-red-500 rounded transition-colors shrink-0"
                    title="Delete feedback"
                  >
                    <HiTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function AdminPage() {
  const { isAdmin } = useAuth()
  const [users, setUsers] = useState([])
  const [departments, setDepartments] = useState([])
  const [organization, setOrganization] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showInvite, setShowInvite] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  async function fetchData() {
    try {
      setLoading(true)
      const [usersRes, deptsRes, orgRes] = await Promise.all([
        authApi.listUsers(),
        departmentsApi.list(),
        authApi.getOrganization().catch(() => ({ data: null })),
      ])
      setUsers(usersRes.data)
      setDepartments(deptsRes.data)
      setOrganization(orgRes.data)
    } catch (err) {
      console.error('Failed to fetch admin data:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleUpdateRole(userId, role) {
    try {
      await authApi.assignRole(userId, role)
    } catch (err) {
      console.error('Failed to update role:', err)
    }
  }

  if (!isAdmin) {
    return (
      <div className="text-center py-16">
        <HiShieldCheck className="w-16 h-16 text-surface-300 dark:text-surface-600 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-surface-800 dark:text-surface-200 mb-2">Access Denied</h2>
        <p className="text-surface-500 dark:text-surface-400">You need admin privileges to access this page.</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-800 dark:text-surface-200">Admin Settings</h1>
        <p className="text-surface-500 dark:text-surface-400 mt-1">Manage users, departments, and organization settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Users Table */}
        <div className="lg:col-span-2 card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <HiUserGroup className="w-5 h-5 text-brand-500" />
              <h2 className="font-semibold text-surface-800 dark:text-surface-200">Users ({users.length})</h2>
            </div>
            <button
              onClick={() => setShowInvite(true)}
              className="btn btn-primary btn-sm"
            >
              <HiUserAdd className="w-4 h-4 mr-1" />
              Invite
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-200 dark:border-surface-700 text-left">
                  <th className="py-2 px-4 text-sm font-medium text-surface-600 dark:text-surface-400">User</th>
                  <th className="py-2 px-4 text-sm font-medium text-surface-600 dark:text-surface-400">Role</th>
                  <th className="py-2 px-4 text-sm font-medium text-surface-600 dark:text-surface-400">Departments</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <UserRow 
                    key={user.id} 
                    user={user} 
                    onUpdateRole={handleUpdateRole}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Departments */}
        <DepartmentSection 
          departments={departments}
          onRefresh={fetchData}
        />
      </div>

      {/* Role Permissions */}
      <RolePermissionsCard />

      {/* User Feedback */}
      <FeedbackSection />

      {/* Invite Modal */}
      {showInvite && (
        <UserInviteModal
          onClose={() => setShowInvite(false)}
          onInvited={fetchData}
        />
      )}
    </div>
  )
}
